import type {
  ProductImageArea,
  ProductImageEntry,
  ProductImageScenario,
} from "./types";

interface Words {
  alt: string;
  shows: string;
  proves: string;
}

const entry = (
  id: string,
  module: string,
  opens: string,
  setup: readonly string[],
  sv: Words,
  en: Words,
  options: { area?: ProductImageArea; scenario?: ProductImageScenario } = {},
): ProductImageEntry => ({
  id,
  module,
  area: options.area ?? "solution",
  opens,
  setup,
  scenario: options.scenario ?? "S1",
  visual: { variant: 0 },
  text: { sv, en },
  use: ["pages/Plattform.tsx"],
});

const SAVED = "Öppna Analys för den sparade ruttlösningen";
const OPTIMIZE = "Öppna Optimera om från Analys";
const HEALTH = "Öppna Analys utan vald lösning";
const REPORT = "Öppna rapporten för den importerade perioden";
const CALENDAR = "Öppna kalendern för den sparade ruttlösningen";

/** Analys, reports and Ask CAIRE surfaces captured in the 2026-10 surface campaign. */
export const CAMPAIGN_ANALYTICS_CATALOGUE: readonly ProductImageEntry[] = [
  entry(
    "analytics.highlights",
    "Analys",
    "Schema → Analys → Viktigast just nu",
    [SAVED, "Läs avsnittet Viktigast just nu"],
    {
      alt: "Avsnittet Viktigast just nu med rangordnade fynd för lösningen",
      shows: "Se de viktigaste fynden i lösningen, rangordnade efter påverkan.",
      proves:
        "Varje fynd anger orsak, åtgärd och vad åtgärden kan ge, rangordnat efter berörda minuter och besök.",
    },
    {
      alt: "Most important right now with the solution's ranked findings",
      shows: "See the solution's most important findings, ranked by impact.",
      proves:
        "Every finding names its cause, its fix and what the fix can give, ranked by the minutes and visits affected.",
    },
  ),
  entry(
    "analytics.quality",
    "Analys",
    "Schema → Analys → Avancerat → Poäng & villkorsanalys",
    [SAVED, "Öppna Poäng & villkorsanalys"],
    {
      alt: "Panelen Schemakvalitet med mål och utfall för lösningen",
      shows:
        "Se hur lösningen står sig mot målen för datakvalitet, brukartidsandel och kontinuitet.",
      proves:
        "Schemakvalitet visar utfallet per mål för den valda lösningen bredvid målvärdet.",
    },
    {
      alt: "Schedule quality panel with goals and results for the solution",
      shows:
        "See how the solution measures up against the goals for data quality, client time share and continuity.",
      proves:
        "Schedule quality shows the result per goal for the selected solution next to its target.",
    },
  ),
  entry(
    "analytics.explore",
    "Analys",
    "Schema → Analys → Utforska",
    [SAVED, "Läs avsnittet Utforska"],
    {
      alt: "Avsnittet Utforska med samma underlag per dimension",
      shows:
        "Utforska lösningens underlag per medarbetare, brukare, distrikt eller tid.",
      proves:
        "Utforska visar samma underlag per dimension med valbart mått och visningssätt.",
    },
    {
      alt: "Explore with the same data per dimension",
      shows:
        "Explore the solution's data per employee, client, district or time.",
      proves:
        "Explore shows the same data per dimension with a selectable measure and view.",
    },
  ),
  entry(
    "analytics.hourly-bottleneck-sheet",
    "Analys",
    "Schema → Analys → Utforska → Flaskhalsar → timme",
    [SAVED, "Välj Flaskhalsar i Utforska", "Klicka på en timme i värmekartan"],
    {
      alt: "Panel för en vald timme med besök, pass, tidsfönster och kapacitet",
      shows: "Se vad som händer i en enskild timme där besök blir otilldelade.",
      proves:
        "Timpanelen visar besök, pass, tidsfönster och kapacitet för den valda timmen i lösningen.",
    },
    {
      alt: "Panel for a selected hour with visits, shifts, time windows and capacity",
      shows: "See what happens in a single hour where visits go unassigned.",
      proves:
        "The hour panel shows visits, shifts, time windows and capacity for the selected hour of the solution.",
    },
  ),
  entry(
    "analytics.optimize-sheet",
    "Analys",
    "Schema → Analys → Optimera om",
    [SAVED, "Välj Optimera om"],
    {
      alt: "Panelen Optimera om med start av en ny optimering och åtgärdscenter",
      shows:
        "Välj åtgärder och starta en ny optimering från den sparade lösningen.",
      proves:
        "Optimera om samlar åtgärderna och startknappen; den sparade lösningen behålls.",
    },
    {
      alt: "Re-optimize panel with a new optimization run and the action center",
      shows:
        "Choose actions and start a new optimization from the saved solution.",
      proves:
        "Re-optimize gathers the actions and the start button; the saved solution is kept.",
    },
  ),
  entry(
    "analytics.actions.evidence-drawer",
    "Analys",
    "Schema → Analys → Optimera om → Visa bevis",
    [
      OPTIMIZE,
      "Öppna Granska data och åtgärder",
      "Välj Visa bevis för en åtgärd",
    ],
    {
      alt: "Bevispanel för en åtgärd med mål, familj och berörd omfattning",
      shows: "Se underlaget bakom en föreslagen åtgärd innan du väljer den.",
      proves:
        "Bevispanelen visar åtgärdens mål, familj, berörd omfattning och underlag.",
    },
    {
      alt: "Evidence panel for an action with goal, family and affected scope",
      shows: "See the evidence behind a proposed action before you choose it.",
      proves:
        "The evidence panel shows the action's goal, family, affected scope and supporting data.",
    },
  ),
  entry(
    "analytics.actions.act-on-filtered-visits",
    "Analys",
    "Schema → Analys → Optimera om → Visa bevis → Vidga besökens tidsfönster",
    [
      OPTIMIZE,
      "Välj Visa bevis för en åtgärd",
      "Läs kortet Vidga besökens tidsfönster",
    ],
    {
      alt: "Kortet Vidga besökens tidsfönster med minuter före och efter",
      shows: "Lägg en vidgning av tidsfönstren för alla besök i ändringarna.",
      proves:
        "Vidgningen läggs i ändringarna och körs via Validera eller Optimera om; vårdplansmallarna ändras inte.",
    },
    {
      alt: "Widen visit time windows card with minutes before and after",
      shows: "Add a time-window widening for all visits to the changes.",
      proves:
        "The widening is added to the changes and runs through Validate or Re-optimize; care-plan templates are not changed.",
    },
  ),
  entry(
    "analytics.actions.promote-time-windows",
    "Analys",
    "Jämför lösningar → Vidga tidsfönster → spara till mallar",
    [
      "Välj två sparade lösningar för samma period i Jämför lösningar",
      "Välj Förhandsgranska",
    ],
    {
      alt: "Kortet Vidga tidsfönster → spara till mallar med förhandsgranskning",
      shows:
        "Förhandsgranska hur validerade tidsfönster sparas till besöksmallarna.",
      proves:
        "Förhandsgranskningen visar vilka mallar som skapas, uppdateras eller hoppas över innan något sparas.",
    },
    {
      alt: "Widen time windows → save to templates card with a preview",
      shows:
        "Preview how validated time windows are saved to the visit templates.",
      proves:
        "The preview lists which templates are created, updated or skipped before anything is saved.",
    },
  ),
  entry(
    "analytics.care-planning-portfolio",
    "Analys",
    "Schema → Analys → Optimera om → Portfölj för omsorgsplanering",
    [OPTIMIZE, "Öppna Portfölj för omsorgsplanering", "Välj Hämta portfölj"],
    {
      alt: "Portfölj för omsorgsplanering för vald ruttlösning och period",
      shows:
        "Granska planerade risker och registrerade observationer för omsorgstagarna i lösningen.",
      proves:
        "Portföljen hämtas för den valda ruttlösningen och perioden och visar risker och observationer per omsorgstagare.",
    },
    {
      alt: "Care-planning portfolio for the selected route solution and period",
      shows:
        "Review planned risks and recorded observations for the clients in the solution.",
      proves:
        "The portfolio is loaded for the selected route solution and period and shows risks and observations per client.",
    },
  ),
  entry(
    "analytics.care-planning-trigger-run",
    "Analys",
    "Schema → Analys → Optimera om → Portfölj → Utlösare för proaktiv granskning",
    [OPTIMIZE, "Hämta portföljen", "Visa körningen för en utlösare"],
    {
      alt: "Granskningskörning startad från en utlösare i portföljen",
      shows:
        "Följ granskningskörningen som en utlösare i portföljen har startat.",
      proves:
        "Körningen visar sin status för den utlösare som planeraren har valt att granska.",
    },
    {
      alt: "Review run started from a trigger in the portfolio",
      shows: "Follow the review run a portfolio trigger has started.",
      proves:
        "The run shows its status for the trigger the planner chose to review.",
    },
  ),
  entry(
    "analytics.care-planning-trigger-watch",
    "Analys",
    "Schema → Analys → Optimera om → Portfölj → Bevakning av portföljutlösare",
    [OPTIMIZE, "Hämta portföljen", "Läs Bevakning av portföljutlösare"],
    {
      alt: "Bevakning av portföljutlösare med aktuell status",
      shows: "Se om portföljen bevakas så att nya utlösare upptäcks.",
      proves:
        "Bevakningen visar sin status för den valda enheten, lösningen och perioden.",
    },
    {
      alt: "Portfolio trigger watch with its current status",
      shows:
        "See whether the portfolio is watched so new triggers are detected.",
      proves:
        "The watch shows its status for the selected unit, solution and period.",
    },
  ),
  entry(
    "analytics.advanced.scores.constraints",
    "Analys",
    "Schema → Analys → Avancerat → Poäng & villkorsanalys → Villkorsanalys",
    [SAVED, "Öppna Poäng & villkorsanalys", "Läs Villkorsanalys"],
    {
      alt: "Villkorsanalys med villkor, typ och antal träffar",
      shows: "Se vilka villkor som påverkar lösningens poäng och hur ofta.",
      proves:
        "Villkorsanalysen listar varje villkor med typ och antal träffar för den sparade lösningen.",
    },
    {
      alt: "Constraint analysis with constraint, type and match count",
      shows: "See which constraints affect the solution's score and how often.",
      proves:
        "The constraint analysis lists every constraint with its type and match count for the saved solution.",
    },
  ),
  entry(
    "analytics.advanced.ledger",
    "Analys",
    "Schema → Analys → Avancerat → Nyckeltal",
    [SAVED, "Öppna Nyckeltal"],
    {
      alt: "Nyckeltal med diagram över tidsfördelning, tilldelning och kontinuitet",
      shows: "Se underlaget bakom lösningens siffror i diagram och tabeller.",
      proves:
        "Nyckeltalen visar betald tid in mot besök, restid, väntan, rast och outnyttjad tid ut.",
    },
    {
      alt: "Metrics with charts for time composition, assignment and continuity",
      shows:
        "See the evidence behind the solution's figures in charts and tables.",
      proves:
        "The metrics show paid time in against visits, travel, waiting, breaks and unused time out.",
    },
  ),
  entry(
    "analytics.advanced.ledger-detail-sheet",
    "Analys",
    "Schema → Analys → Avancerat → Nyckeltal → välj en rad",
    [
      SAVED,
      "Öppna Nyckeltal",
      "Välj en medarbetare i tabellen Per medarbetare",
    ],
    {
      alt: "Medarbetardetaljer för en vald rad i nyckeltalen",
      shows: "Öppna en rad i nyckeltalen och se detaljerna för den.",
      proves:
        "Detaljpanelen visar den valda medarbetarens siffror och pass ur samma underlag som tabellen.",
    },
    {
      alt: "Employee details for a selected row in the metrics",
      shows: "Open a row in the metrics and see its details.",
      proves:
        "The detail panel shows the selected employee's figures and shifts from the same data as the table.",
    },
  ),
  entry(
    "analytics.advanced.solutions",
    "Analys",
    "Schema → Analys → Avancerat → Lösningar",
    [SAVED, "Öppna Lösningar"],
    {
      alt: "Lösningar på schemat med val av lösning",
      shows: "Välj lösning och se alla lösningar på schemat.",
      proves:
        "Lösningar listar schemats sparade lösningar och byter den lösning Analys läser.",
    },
    {
      alt: "Solutions on the schedule with solution selection",
      shows: "Choose a solution and see every solution on the schedule.",
      proves:
        "Solutions lists the schedule's saved solutions and switches the solution Analysis reads.",
    },
  ),
  entry(
    "analytics.verification",
    "Analys",
    "Schema → Analys → Avancerat → Datakontroll",
    [SAVED, "Öppna Avancerat", "Läs Datakontroll"],
    {
      alt: "Datakontroll med status, kontrollerade besök och fynd",
      shows: "Se om lösningens besök stämmer mot sitt underlag.",
      proves:
        "Datakontrollen visar hur många besök som kontrollerats och antalet fel, ofullständiga poster och avvikelser.",
    },
    {
      alt: "Data integrity with status, visits checked and findings",
      shows: "See whether the solution's visits match their source data.",
      proves:
        "Data integrity shows how many visits were checked and the number of errors, incomplete records and deviations.",
    },
  ),
  entry(
    "analytics.verification.records",
    "Analys",
    "Schema → Analys → Avancerat → Datakontroll → Besöksunderlag",
    [SAVED, "Öppna Datakontroll", "Öppna Besöksunderlag"],
    {
      alt: "Besöksunderlag per post med täckning, källintegritet och genomförbarhet",
      shows: "Gå igenom underlaget för varje besök, pass eller hela lösningen.",
      proves:
        "Varje post visar täckning, källintegritet, genomförbarhet och kontrollresultat.",
    },
    {
      alt: "Visit evidence per record with coverage, source integrity and feasibility",
      shows:
        "Go through the evidence for each visit, shift or the whole solution.",
      proves:
        "Each record shows coverage, source integrity, feasibility and the check result.",
    },
  ),
  entry(
    "analytics.health.input-health",
    "Schemahälsa",
    "Schema → Analys → Schemahälsa",
    [HEALTH],
    {
      alt: "Schemahälsa med indatakvalitet innan schemat optimeras",
      shows: "Se kvaliteten på schemats indata innan du optimerar.",
      proves:
        "Schemahälsa sammanfattar indata och visar vad som behöver åtgärdas före nästa körning.",
    },
    {
      alt: "Schedule health with input quality before the schedule is optimized",
      shows: "See the quality of the schedule's input before you optimize.",
      proves:
        "Schedule health summarises the input and shows what needs fixing before the next run.",
    },
    { area: "schedule" },
  ),
  entry(
    "analytics.health.priority-risk",
    "Schemahälsa",
    "Schema → Analys → Schemahälsa → Visa detaljer → Prioritet-1 i risk",
    [HEALTH, "Välj Visa detaljer"],
    {
      alt: "Prioritet-1 i risk med exponerade besök",
      shows: "Se vilka prioritet-1-besök som riskerar att inte bli utförda.",
      proves:
        "Panelen räknar prioritet-1-besök som är exponerade mot kapacitetsgap eller granskningsvarningar.",
    },
    {
      alt: "Priority 1 at risk with exposed visits",
      shows:
        "See which priority-1 visits are at risk of not being carried out.",
      proves:
        "The panel counts priority-1 visits exposed to capacity gaps or review warnings.",
    },
    { area: "schedule" },
  ),
  entry(
    "analytics.health.issue-detail-sheet",
    "Schemahälsa",
    "Schema → Analys → Schemahälsa → Visa detaljer → öppna ett överlapp",
    [HEALTH, "Välj Visa detaljer", "Öppna ett olöst överlapp"],
    {
      alt: "Dagtidslinje för en brukare med två överlappande besök markerade",
      shows: "Öppna ett överlapp och se brukarens dag med de berörda besöken.",
      proves:
        "Panelen visar brukarens dagtidslinje och åtgärderna för att lösa överlappet.",
    },
    {
      alt: "Day timeline for a client with two overlapping visits highlighted",
      shows:
        "Open an overlap and see the client's day with the visits involved.",
      proves:
        "The panel shows the client's day timeline and the actions that resolve the overlap.",
    },
    { area: "schedule" },
  ),
  entry(
    "analytics.health.merge-visits-dialog",
    "Schemahälsa",
    "Schema → Analys → Schemahälsa → överlapp → Slå ihop besök",
    [HEALTH, "Öppna ett olöst överlapp", "Välj Slå ihop besök"],
    {
      alt: "Dialogen Slå ihop besök med besökens ordning",
      shows:
        "Slå ihop två överlappande besök så att de utförs direkt efter varandra.",
      proves:
        "Dialogen kedjar besöken i vald ordning och kan även spara sammanslagningen på vårdplanen.",
    },
    {
      alt: "Merge visits dialog with the order of the visits",
      shows: "Merge two overlapping visits so they run back to back.",
      proves:
        "The dialog chains the visits in the chosen order and can also save the merge on the care plan.",
    },
    { area: "schedule" },
  ),
  entry(
    "analytics.health.defer-visits-dialog",
    "Schemahälsa",
    "Schema → Analys → Schemahälsa → Rekommendationer → Skjut upp flexibla besök",
    [HEALTH, "Välj Skjut upp flexibla besök i Rekommendationer"],
    {
      alt: "Dialogen Skjut upp flexibla besök med val av riktning",
      shows:
        "Flytta flexibla besök från topptimmen till dagen före eller efter.",
      proves:
        "Dialogen anger hur många besök som kan skjutas upp; obligatoriska och låsta besök hoppas över.",
    },
    {
      alt: "Defer flexible visits dialog with a choice of direction",
      shows:
        "Move flexible visits from the peak hour to the day before or after.",
      proves:
        "The dialog states how many visits can be deferred; mandatory and locked visits are skipped.",
    },
    { area: "schedule" },
  ),
  entry(
    "analytics.health.preferred-pool-dialog",
    "Schemahälsa",
    "Schema → Analys → Schemahälsa → Rekommendationer → Begränsa personalpool",
    [HEALTH, "Välj Begränsa personalpool i Rekommendationer"],
    {
      alt: "Dialogen Sätt önskad vårdgivarpool för en brukare",
      shows: "Spara brukarens vanligaste vårdgivare som önskemål.",
      proves:
        "Dialogen visar hur många olika vårdgivare brukaren har i lösningen och sparar de vanligaste som önskemål.",
    },
    {
      alt: "Set preferred caregiver pool dialog for a client",
      shows: "Save the client's most frequent caregivers as preferences.",
      proves:
        "The dialog shows how many different caregivers the client has in the solution and saves the most frequent as preferences.",
    },
    { area: "schedule" },
  ),
  entry(
    "analytics.pending-state",
    "Analys",
    "Schema → Analys → en lösning utan ruttresultat",
    ["Välj en skiftlösning som ännu saknar rutt i Analys"],
    {
      alt: "Meddelande om att ruttresultat saknas för den valda lösningen",
      shows:
        "Analys säger tydligt när den valda lösningen saknar ruttresultat.",
      proves:
        "Detaljanalysen visas först för en färdig ruttlösning, och sidan anger vilken rad som är vald.",
    },
    {
      alt: "Notice that route results are missing for the selected solution",
      shows:
        "Analysis says clearly when the selected solution has no route results.",
      proves:
        "Detailed analysis appears only for a completed route solution, and the page names the selected row.",
    },
  ),
  entry(
    "analytics.legacy-frontier-cards",
    "Analys",
    "Schema → Analys → Lösningsförslag",
    [
      "Öppna Analys för ett schema med lösningsförslag från en tidigare körning",
    ],
    {
      alt: "Lösningsförslag som kort med status per förslag",
      shows: "Se lösningsförslagen från en körning som kort med status.",
      proves:
        "Varje kort visar förslagets status och öppnar lösningen när den är klar.",
    },
    {
      alt: "Solution proposals as cards with a status per proposal",
      shows:
        "See the solution proposals from a run as cards with their status.",
      proves:
        "Each card shows the proposal's status and opens the solution once it is ready.",
    },
  ),
  entry(
    "analytics.analysis-page.load-failed",
    "Analys",
    "Schema → ett schema som inte finns",
    ["Öppna en länk till ett schema som har tagits bort"],
    {
      alt: "Meddelandet Schema hittades inte med knapp tillbaka till scheman",
      shows: "En länk till ett borttaget schema ger ett tydligt besked.",
      proves:
        "Sidan säger att schemat inte hittas och leder tillbaka till listan med scheman.",
    },
    {
      alt: "Schedule not found message with a button back to schedules",
      shows: "A link to a deleted schedule gives a clear answer.",
      proves:
        "The page says the schedule cannot be found and leads back to the schedule list.",
    },
  ),
  entry(
    "analytics.frontier-curve.launch-failed",
    "Analys",
    "Schema → Analys → Lösningsförslag → körningen kunde inte starta",
    ["Öppna Analys för ett schema vars senaste körning misslyckades"],
    {
      alt: "Meddelandet Optimeringen kunde inte starta med felkod",
      shows: "Se när en körning inte kunde starta för något förslag.",
      proves:
        "Meddelandet anger att alla förslag misslyckades och visar den kod som supporten behöver.",
    },
    {
      alt: "The optimization could not start message with an error code",
      shows: "See when a run could not start for any proposal.",
      proves:
        "The message states that every proposal failed and shows the code support needs.",
    },
  ),
  entry(
    "analytics.unassigned-reasons",
    "Analys",
    "Kalender → Validera → Valideringsresultat",
    [CALENDAR, "Välj Validera och bekräfta", "Läs Varför besök är otilldelade"],
    {
      alt: "Valideringsresultat med orsaker till otilldelade besök",
      shows: "Se varför besök blir otilldelade när en lösning valideras.",
      proves:
        "Valideringsresultatet grupperar de otilldelade besöken efter orsak med antal per orsak.",
    },
    {
      alt: "Validation result with the reasons visits are unassigned",
      shows: "See why visits go unassigned when a solution is validated.",
      proves:
        "The validation result groups the unassigned visits by reason with a count per reason.",
    },
  ),
  entry(
    "analytics.advanced.scores.resources",
    "Analys",
    "Kalender → Validera → Valideringsresultat → Resurser",
    [CALENDAR, "Välj Validera och bekräfta", "Välj fliken Resurser"],
    {
      alt: "Fliken Resurser med besök, medarbetare och brukare som har villkorsbrott",
      shows:
        "Se vilka besök, medarbetare och brukare som berörs av villkorsbrott.",
      proves:
        "Resurser listar de berörda med allvarlighetsgrad och antal träffar, med länk till varje post.",
    },
    {
      alt: "Resources tab with visits, employees and clients that have violations",
      shows:
        "See which visits, employees and clients are affected by violations.",
      proves:
        "Resources lists those affected with severity and match count, linking to each record.",
    },
  ),
  entry(
    "analytics.actions.launch-outcome",
    "Analys",
    "Schema → Analys → Optimera om → Skapa och optimera ny lösning",
    [OPTIMIZE, "Välj Skapa och optimera ny lösning"],
    {
      alt: "Resultatet av den senaste körningen i Optimera om",
      shows: "Se vad som hände när en ny optimering startades från lösningen.",
      proves:
        "Meddelandet visar hur körningen startades och hur många åtgärder den tog med.",
    },
    {
      alt: "Result of the last run in Re-optimize",
      shows:
        "See what happened when a new optimization started from the solution.",
      proves:
        "The notice shows how the run started and how many actions it included.",
    },
  ),
  entry(
    "reports.analytics-explorer",
    "Rapporter",
    "Rapporter → Veckorapport → Anpassa",
    [
      "Öppna Rapporter för enheten och välj Veckorapport",
      "Ange den publicerade perioden under Anpassa",
    ],
    {
      alt: "Sidan Rapporter med nyckeltal för publicerade lösningar",
      shows: "Följ enhetens nyckeltal över tid ur de publicerade lösningarna.",
      proves:
        "Rapporter räknar nyckeltalen ur publicerade lösningar för vald enhet och period.",
    },
    {
      alt: "Reports page with key figures for published solutions",
      shows:
        "Follow the unit's key figures over time from the published solutions.",
      proves:
        "Reports computes the key figures from published solutions for the selected unit and period.",
    },
  ),
  entry(
    "reports.analytics.comparison",
    "Rapporter",
    "Rapporter → Jämför (en vecka mot en annan)",
    [
      "Öppna Rapporter och välj enhetens publicerade schema",
      "Välj Jämför; B är enhetens föregående schema",
    ],
    {
      alt: "Jämför två urval med skillnaden B minus A per nyckeltal",
      shows: "Jämför två urval av perioder, enheter eller rader med varandra.",
      proves:
        "Jämförelsen visar skillnaden B minus A per nyckeltal och kräver jämförbart underlag.",
    },
    {
      alt: "Compare two selections with the difference B minus A per key figure",
      shows:
        "Compare two selections of periods, units or rows with each other.",
      proves:
        "The comparison shows the difference B minus A per key figure and requires compatible evidence.",
    },
  ),
  entry(
    "reports.section.overview",
    "Rapport",
    "Schema → rapport → Översikt",
    [REPORT],
    {
      alt: "Rapportens översikt för den importerade perioden",
      shows: "Börja rapporten med en översikt över planen och perioden.",
      proves:
        "Översikten sammanfattar den importerade perioden ur samma underlag som resten av rapporten.",
    },
    {
      alt: "The report's overview for the imported period",
      shows: "Open the report with an overview of the plan and the period.",
      proves:
        "The overview summarises the imported period from the same data as the rest of the report.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.section.issues",
    "Rapport",
    "Schema → rapport → Vad brister",
    [REPORT, "Ta med avsnittet Vad brister"],
    {
      alt: "Rapportavsnittet Vad brister i den importerade planen",
      shows: "Se vad i planen som inte håller.",
      proves:
        "Avsnittet listar det som brister i den importerade planen med underlag från perioden.",
    },
    {
      alt: "The report section on what needs attention in the imported plan",
      shows: "See what in the plan does not hold.",
      proves:
        "The section lists what needs attention in the imported plan with evidence from the period.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.section.plan-fixed",
    "Rapport",
    "Schema → rapport → Er plan, åtgärdad",
    [REPORT, "Ta med avsnittet Er plan, åtgärdad"],
    {
      alt: "Rapportavsnittet Er plan, åtgärdad",
      shows: "Se planen när bristerna har åtgärdats.",
      proves:
        "Avsnittet visar samma period med de åtgärdade bristerna i Caires lösning.",
    },
    {
      alt: "The report section Your plan, improved",
      shows: "See the plan once the issues have been fixed.",
      proves:
        "The section shows the same period with the issues fixed in Caire's solution.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.section.value",
    "Rapport",
    "Schema → rapport → Vad det är värt",
    [REPORT, "Ta med avsnittet Vad det är värt"],
    {
      alt: "Rapportavsnittet Vad det är värt",
      shows: "Se vad förbättringen är värd för verksamheten.",
      proves:
        "Avsnittet räknar värdet av förbättringen ur rapportens egna siffror för perioden.",
    },
    {
      alt: "The report section What it is worth",
      shows: "See what the improvement is worth to the organisation.",
      proves:
        "The section computes the value of the improvement from the report's own figures for the period.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.section.next-step",
    "Rapport",
    "Schema → rapport → Nästa steg",
    [REPORT, "Ta med avsnittet Nästa steg"],
    {
      alt: "Rapportavsnittet Nästa steg",
      shows: "Avsluta rapporten med vad nästa steg är.",
      proves:
        "Nästa steg utgår från rapportens resultat och anger hur ni går vidare.",
    },
    {
      alt: "The report section Next step",
      shows: "Close the report with the next step.",
      proves:
        "The next step builds on the report's results and states how to move forward.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.block.flex-windows",
    "Rapport",
    "Schema → rapport → Caires rekommendationer → Hur långt får starten flyttas?",
    [REPORT, "Ta med avsnittet Caires rekommendationer"],
    {
      alt: "Diagrammet Hur långt får starten flyttas? per besökskategori",
      shows: "Se hur mycket besökens starttid får flyttas i Caires plan.",
      proves:
        "Diagrammet visar det tillåtna startfönstret per besökskategori i Caires plan.",
    },
    {
      alt: "How far may the start move? chart per visit category",
      shows: "See how far visit start times may move in Caire's plan.",
      proves:
        "The chart shows the allowed start window per visit category in Caire's plan.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.section.shift-catalogue",
    "Rapport",
    "Schema → rapport → Bilaga → Arbetspassmallar",
    [REPORT, "Ta med Bilaga: detaljerade jämförelser"],
    {
      alt: "Bilagan Arbetspassmallar som lösningen använder",
      shows: "Se vilka arbetspassmallar lösningen bygger på.",
      proves:
        "Bilagan listar arbetspassmallarna som lösningen använder och anger var uppgifterna kommer ifrån.",
    },
    {
      alt: "Appendix Shift templates used by the solution",
      shows: "See which shift templates the solution is built on.",
      proves:
        "The appendix lists the shift templates the solution uses and states where the data comes from.",
    },
    { scenario: "S4" },
  ),
  entry(
    "reports.section.detail-appendix",
    "Rapport",
    "Schema → rapport → Bilaga: detaljerade jämförelser",
    [REPORT, "Ta med Bilaga: detaljerade jämförelser"],
    {
      alt: "Bilaga med detaljerade jämförelser för perioden",
      shows: "Gå igenom de detaljerade jämförelserna bakom rapporten.",
      proves:
        "Bilagan samlar nyckeltal, restid och rekommendationer i detalj ur samma underlag som rapporten.",
    },
    {
      alt: "Appendix with detailed comparisons for the period",
      shows: "Go through the detailed comparisons behind the report.",
      proves:
        "The appendix gathers key figures, travel and recommendations in detail from the same data as the report.",
    },
    { scenario: "S4" },
  ),
  entry(
    "ask-caire.calendar-action-proposal",
    "Fråga CAIRE",
    "Kalender → Lösningsutkast från Caire",
    [CALENDAR, "Be Caire förbereda ett lösningsutkast"],
    {
      alt: "Lösningsutkast från Caire i kalendern med risknivå och förhandsgranskning",
      shows: "Granska Caires lösningsutkast i kalendern innan något körs.",
      proves:
        "Utkastet visar risknivå och påverkan; ingen körning startar förrän planeraren skickar det.",
    },
    {
      alt: "Solve draft from Caire in the calendar with risk level and preview",
      shows: "Review Caire's solve draft in the calendar before anything runs.",
      proves:
        "The draft shows its risk level and impact; no run starts until the planner sends it.",
    },
    { area: "ai", scenario: "S6" },
  ),
  entry(
    "ask-caire.recommendation-selection",
    "Fråga CAIRE",
    "Kalender → Lösningsutkast från Caire → Välj en sparad rekommendation",
    [CALENDAR, "Be Caire rekommendera en tid för ett otilldelat besök"],
    {
      alt: "Välj en sparad rekommendation med rangordnade alternativ",
      shows: "Välj bland Caires rangordnade alternativ för besöket.",
      proves:
        "Alternativen är sparade med poäng och medarbetare, och planeraren väljer vilket som gäller.",
    },
    {
      alt: "Choose a saved recommendation with ranked options",
      shows: "Choose among Caire's ranked options for the visit.",
      proves:
        "The options are saved with a score and an employee, and the planner chooses which one applies.",
    },
    { area: "ai", scenario: "S6" },
  ),
  entry(
    "ask-caire.action-proposal-result",
    "Fråga CAIRE",
    "Fråga CAIRE → åtgärdsförslag → Databasresultat",
    [CALENDAR, "Öppna Fråga CAIRE för ett genomfört åtgärdsförslag"],
    {
      alt: "Databasresultat för ett genomfört åtgärdsförslag",
      shows: "Se exakt vad ett godkänt åtgärdsförslag ändrade.",
      proves:
        "Resultatet visar de ändrade posterna efter genomförandet med länk till schemat eller lösningen.",
    },
    {
      alt: "Database result for an executed action proposal",
      shows: "See exactly what an approved action proposal changed.",
      proves:
        "The result shows the changed records after execution with a link to the schedule or solution.",
    },
    { area: "ai", scenario: "S6" },
  ),
  entry(
    "ask-caire.free-analysis-panel",
    "Fråga CAIRE",
    "Gratis analys → Fråga CAIRE",
    ["Öppna en gratis analys med resultat", "Välj Fråga CAIRE"],
    {
      alt: "Fråga CAIRE i den gratis analysen med föreslagna frågor",
      shows:
        "Ställ frågor om er analys: begränsningar, nyckeltal och förbättringar.",
      proves:
        "Panelen visar hur många frågor som återstår, och svaren förklarar utan att ändra något.",
    },
    {
      alt: "Ask CAIRE in the free analysis with suggested questions",
      shows:
        "Ask about your analysis: limitations, key figures and improvements.",
      proves:
        "The panel shows how many questions remain, and the answers explain without changing anything.",
    },
    { area: "ai", scenario: "S5" },
  ),
];
